import { loadStripe, Stripe } from '@stripe/stripe-js'
import { supabase } from './supabase'

// Initialize Stripe (you'll need to add your publishable key to env vars)
let stripePromise: Promise<Stripe | null>

const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY!)
  }
  return stripePromise
}

export interface PlanFeatures {
  maxUsers: number | 'unlimited'
  dailySummaries: boolean
  advancedAnalytics: boolean
  price: number
  pricePerUser?: boolean
}

export const PLAN_CONFIGS: Record<string, PlanFeatures> = {
  trial: {
    maxUsers: 4, // Trial users get essentials features
    dailySummaries: true,
    advancedAnalytics: false,
    price: 0
  },
  advanced_trial: {
    maxUsers: 'unlimited', // Full advanced features during trial
    dailySummaries: true,
    advancedAnalytics: true,
    price: 0 // Free trial
  },
  essentials: {
    maxUsers: 4,
    dailySummaries: true,
    advancedAnalytics: false,
    price: 1.49, // Match Stripe price: $1.49/user/month
    pricePerUser: true // Per user pricing
  },
  advanced: {
    maxUsers: 'unlimited',
    dailySummaries: true,
    advancedAnalytics: true,
    price: 2.00, // Match Stripe price: $2.00/user/month
    pricePerUser: true // Per user pricing
  }
}

export interface SubscriptionStatus {
  subscription: {
    subscription_id: string
    status: string
    plan_name: string
    current_period_end: string
    cancel_at_period_end: boolean
  } | null
  member_count: number
  team_plan?: string
  team_created_at?: string
}

// Billing management functions
export const createCheckoutSession = async (teamId: string, planName: string, userId: string) => {
  console.log('=== CHECKOUT SESSION DEBUG ===')
  console.log('Team ID:', teamId)
  console.log('Plan Name:', planName) 
  console.log('User ID:', userId)
  console.log('==============================')

  const { data, error } = await supabase.functions.invoke('billing-management', {
    body: {
      action: 'create_checkout_session',
      teamId,
      planName,
      userId
    }
  })

  if (error) {
    console.error('Error creating checkout session:', error)
    // Try to extract detailed error message from response
    try {
      const errorResponse = await error.response?.json?.()
      if (errorResponse?.details) {
        console.error('Detailed error:', errorResponse.details)
        throw new Error(`Checkout failed: ${errorResponse.details}`)
      }
    } catch (parseError) {
      console.error('Could not parse error details:', parseError)
    }
    throw error
  }

  return data.checkout_url
}

export const createPortalSession = async (teamId: string, userId: string) => {
  console.log('=== PORTAL SESSION DEBUG ===')
  console.log('Team ID:', teamId)
  console.log('User ID:', userId)
  console.log('=============================')

  try {
    const { data, error } = await supabase.functions.invoke('billing-management', {
      body: {
        action: 'create_portal_session',
        teamId,
        userId
      }
    })

    console.log('Portal session response:', { data, error })

    if (error) {
      console.error('Error creating portal session:', error)
      console.error('Error details:', JSON.stringify(error, null, 2))
      
      // Try to extract error details from the 400 response
      if (error.name === 'FunctionsHttpError' || error.message?.includes('non-2xx status code')) {
        console.error('FunctionsHttpError detected, trying to get error details')
        
        try {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
          const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
          
          const response = await fetch(`${supabaseUrl}/functions/v1/billing-management`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${supabaseAnonKey}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              action: 'create_portal_session',
              teamId,
              userId
            })
          })
          
          if (!response.ok) {
            const errorData = await response.json()
            console.log('Error response from Edge Function:', errorData)
            
            if (errorData.needsSubscription) {
              const subscriptionError = new Error(errorData.error)
              subscriptionError.name = 'SubscriptionRequiredError'
              ;(subscriptionError as any).needsSubscription = true
              ;(subscriptionError as any).teamPlan = errorData.teamPlan
              throw subscriptionError
            }
            
            throw new Error(errorData.error || 'Failed to open billing portal')
          }
          
          const responseData = await response.json()
          return responseData.portal_url
          
        } catch (fetchError: any) {
          // If it's a SubscriptionRequiredError, re-throw it
          if (fetchError.name === 'SubscriptionRequiredError') {
            throw fetchError
          }
          
          console.error('Failed to fetch error details:', fetchError)
          throw new Error('Failed to open billing portal. Please try again.')
        }
      }
      
      throw error
    }

    // Handle the case where the team needs to subscribe first
    if (data && data.needsSubscription) {
      const subscriptionError = new Error(data.error)
      subscriptionError.name = 'SubscriptionRequiredError'
      ;(subscriptionError as any).needsSubscription = true
      ;(subscriptionError as any).teamPlan = data.teamPlan
      throw subscriptionError
    }

    return data.portal_url
  } catch (error: any) {
    // Re-throw SubscriptionRequiredError without modification
    if (error.name === 'SubscriptionRequiredError') {
      throw error
    }
    
    console.error('Portal session error details:', error)
    throw error
  }
}

export const getSubscriptionStatus = async (teamId: string, userId: string): Promise<SubscriptionStatus> => {
  try {
    const { data, error } = await supabase.functions.invoke('billing-management', {
      body: {
        action: 'get_subscription_status',
        teamId,
        userId
      }
    })

    if (error) {
      console.error('Error getting subscription status:', error)
      // Add more context to the error for better debugging
      const enhancedError = new Error(`Billing check failed: ${error.message || 'Unknown error'}`)
      enhancedError.name = 'BillingCheckError'
      throw enhancedError
    }

    return data
  } catch (error) {
    // If it's a network error, add context
    if (error instanceof Error && error.message.includes('Failed to send a request')) {
      const networkError = new Error('Network error while checking subscription status - this may be temporary')
      networkError.name = 'NetworkError'
      throw networkError
    }
    
    // Re-throw other errors as-is
    throw error
  }
}

export const canAddTeamMember = async (teamId: string): Promise<boolean> => {
  // For now, let's implement this check on the frontend
  // Later this can be moved to a proper RPC function
  try {
    // Get current member count
    const { count: memberCount } = await supabase
      .from('team_members')
      .select('*', { count: 'exact', head: true })
      .eq('team_id', teamId)

    // Get team plan
    const { data: team, error: teamError } = await supabase
      .from('teams')
      .select('plan')
      .eq('id', teamId)
      .single()

    if (teamError || !team) {
      console.error('Error getting team plan:', teamError)
      return false
    }

    const plan = team.plan || 'trial'
    const features = getPlanFeatures(plan)
    
    if (typeof features.maxUsers === 'number') {
      return (memberCount || 0) < features.maxUsers
    }
    
    // Unlimited users for advanced plan
    return true
  } catch (error) {
    console.error('Error checking team member limit:', error)
    return false
  }
}

// Helper functions for plan features
export const getPlanFeatures = (planName: string): PlanFeatures => {
  return PLAN_CONFIGS[planName] || PLAN_CONFIGS.trial
}

export const calculatePlanPrice = (planName: string, userCount: number): number => {
  const plan = getPlanFeatures(planName)
  
  if (planName === 'trial') {
    return 0
  }
  
  // Both essentials and advanced are now per-user pricing
  if (planName === 'essentials' || planName === 'advanced') {
    // Show minimum price of 1 user if userCount is 0
    const effectiveUserCount = Math.max(1, userCount)
    return plan.price * effectiveUserCount
  }
  
  return 0
}

export const formatPrice = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount)
}

export const getUpgradeRecommendation = (currentPlan: string, userCount: number): string | null => {
  if (currentPlan === 'trial' && userCount > 4) {
    return 'advanced'
  }
  
  if (currentPlan === 'essentials' && userCount > 4) {
    return 'advanced'
  }
  
  return null
}

export const getPlanDisplayName = (planName: string): string => {
  // Handle potential plan name variations
  const normalizedPlan = planName?.toLowerCase()?.trim()
  
  switch (normalizedPlan) {
    case 'trial':
      return 'Trial'
    case 'advanced_trial':
      return 'Advanced Trial'
    case 'essentials':
    case 'essential':
      return 'Essentials'
    case 'advanced':
    case 'pro':
    case 'premium':
      return 'Advanced'
    default:
      // Debug: log unknown plan names
      console.warn('Unknown plan name:', planName)
      return `Plan (${planName || 'Unknown'})`
  }
}

export const checkTrialStatus = (subscriptionStatus: SubscriptionStatus | null, teamCreatedAt?: string) => {
  if (!subscriptionStatus) return { isTrialExpired: true, isOnTrial: false, daysRemaining: 0 }
  
  const { subscription, team_plan } = subscriptionStatus
  
  // If they have an active paid subscription, they're not on trial
  if (subscription && subscription.status === 'active' && !['trial', 'advanced_trial'].includes(subscription.plan_name)) {
    return { isTrialExpired: false, isOnTrial: false, daysRemaining: 0 }
  }
  
  // If they're on a trial subscription (Stripe trial)
  if (subscription && subscription.status === 'trialing') {
    const trialEnd = new Date(subscription.current_period_end)
    const now = new Date()
    const daysRemaining = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    
    return {
      isTrialExpired: daysRemaining <= 0,
      isOnTrial: true,
      daysRemaining: Math.max(0, daysRemaining),
      trialEndDate: trialEnd
    }
  }
  
  // If subscription is cancelled, expired, or inactive - trial is expired
  if (subscription && ['canceled', 'cancelled', 'expired', 'incomplete_expired', 'unpaid'].includes(subscription.status)) {
    return { isTrialExpired: true, isOnTrial: false, daysRemaining: 0 }
  }
  
  // If they're on team trial (no subscription yet) - handles both 'trial' and 'advanced_trial'
  if (team_plan && ['trial', 'advanced_trial'].includes(team_plan) && !subscription) {
    // Check if team was created more than 7 days ago
    if (teamCreatedAt) {
      const teamCreated = new Date(teamCreatedAt)
      const trialEnd = new Date(teamCreated)
      trialEnd.setDate(trialEnd.getDate() + 7)
      
      const now = new Date()
      const daysRemaining = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      
      return {
        isTrialExpired: daysRemaining <= 0,
        isOnTrial: true,
        daysRemaining: Math.max(0, daysRemaining),
        trialEndDate: trialEnd
      }
    }
    
    // If we don't have team creation date, assume trial is still active for now
    return { isTrialExpired: false, isOnTrial: true, daysRemaining: 7 }
  }
  
  // Default: if no active subscription and not explicitly on trial, trial is expired
  return { isTrialExpired: true, isOnTrial: false, daysRemaining: 0 }
}

export default getStripe